import { ref, watch } from 'vue';

import { defineStore } from 'pinia';

import { reviewCount } from '@/helpers/mutations';
import { mutationsApi } from '@/services/mcp/mutations-api.service';
import { webSocketService } from '@/services/websocket';
import { useAccountStore } from '@/stores/account';

const PING_DEBOUNCE_MS = 1000;
const QUEUE_FRESH_MS = 5000;
const POLL_MS = 45000;

/**
 * To-review count shared by the header and navigation badges.
 *
 * The queue writes the number it already fetched (`setFromQueue`). Everywhere
 * else this store reads the badge endpoint. A `mutation_changed` ping refreshes
 * unless that queue write is younger than five seconds, so an open queue and
 * the badge do not fetch the same change twice. GET failures stay silent:
 * gateGuard ignores reads, and the axios error interceptor rejects without a toast.
 */
export const useMutationReviewBadgeStore = defineStore('mutationReviewBadge', () => {
  const account = useAccountStore();
  const count = ref(0);
  const bp = ref(null);
  const setAt = ref(0);

  // account.currentBusinessProfile is the reactive selection (it keeps bpSelection in step).
  // bpSelection.get() is a plain module variable, so a watch on it never fires when the
  // header badge stays mounted across a profile switch.
  const currentBp = () => account.currentBusinessProfile?.id ?? null;

  // Set only by the queue, so a badge-originated refresh does not suppress the next ping.
  let queueSetAt = 0;
  // Bumped on start/stop so an in-flight read from the previous session cannot
  // repaint the count after logout (the store survives a reload-free sign-out).
  let epoch = 0;
  // The poll, a ping and a visibility change can each call refresh while a slow
  // read for this profile is still out. Drop that duplicate. A new epoch or
  // profile still fetches: the checks below discard the in-flight answer.
  let refreshFlight = null;
  let running = false;
  let pingTimer = null;
  let pollTimer = null;
  let unsubscribe = null;
  let stopBpWatch = null;

  function clearCount() {
    count.value = 0;
    bp.value = null;
    setAt.value = 0;
    queueSetAt = 0;
  }

  async function refresh() {
    const requested = currentBp();
    const epochAtRequest = epoch;
    if (requested == null || requested === '') return;
    if (refreshFlight && refreshFlight.epoch === epochAtRequest
      && String(refreshFlight.bp) === String(requested)) return;
    const flight = { epoch: epochAtRequest, bp: requested };
    refreshFlight = flight;
    try {
      const data = await mutationsApi.badge();
      if (epochAtRequest !== epoch) return;
      if (String(currentBp() ?? '') !== String(requested)) return;
      const next = reviewCount(data);
      count.value = Number.isInteger(next) ? next : 0;
      bp.value = requested;
      setAt.value = Date.now();
    } catch {
      // Leave the last count. Nothing toasts a failed read.
    } finally {
      if (refreshFlight === flight) refreshFlight = null;
    }
  }

  function setFromQueue(counts, businessProfile) {
    if (businessProfile == null || businessProfile === '') return;
    if (String(currentBp() ?? '') !== String(businessProfile)) return;
    const next = reviewCount(counts);
    count.value = Number.isInteger(next) ? next : 0;
    bp.value = businessProfile;
    const now = Date.now();
    setAt.value = now;
    queueSetAt = now;
  }

  function onPing() {
    if (!running) return;
    clearTimeout(pingTimer);
    pingTimer = setTimeout(() => {
      if (!running) return;
      if (Date.now() - queueSetAt < QUEUE_FRESH_MS) return;
      refresh();
    }, PING_DEBOUNCE_MS);
  }

  function armPoll() {
    clearTimeout(pollTimer);
    pollTimer = setTimeout(() => {
      if (!running) return;
      if (!document.hidden) refresh();
      armPoll();
    }, POLL_MS);
  }

  function onVisibility() {
    if (!running || document.hidden) return;
    if (Date.now() - setAt.value >= POLL_MS) refresh();
  }

  function start() {
    if (running) return;
    running = true;
    epoch += 1;
    clearCount();
    unsubscribe = webSocketService.subscribe('mutation_changed', onPing);
    stopBpWatch = watch(currentBp, (next, prev) => {
      if (String(next ?? '') === String(prev ?? '')) return;
      count.value = 0;
      bp.value = next ?? null;
      if (next != null && next !== '') refresh();
    });
    document.addEventListener('visibilitychange', onVisibility);
    armPoll();
    refresh();
  }

  function stop() {
    if (!running) return;
    running = false;
    epoch += 1;
    clearCount();
    clearTimeout(pingTimer);
    pingTimer = null;
    clearTimeout(pollTimer);
    pollTimer = null;
    unsubscribe?.();
    unsubscribe = null;
    stopBpWatch?.();
    stopBpWatch = null;
    document.removeEventListener('visibilitychange', onVisibility);
  }

  return {
    count,
    bp,
    setAt,
    refresh,
    setFromQueue,
    start,
    stop,
  };
});
